<template>
    <div class="seat-map-canvas">
        <div class="canvas-controls">
            <button 
                @click="zoomIn" 
                class="zoom-btn zoom-in" 
                title="Zoom In"
                :disabled="currentZoom >= maxZoom"
            >
                +
            </button>
            <button 
                @click="zoomOut" 
                class="zoom-btn zoom-out" 
                title="Zoom Out"
                :disabled="currentZoom <= minZoom"
            >
                -
            </button>
            <button 
                @click="resetZoom" 
                class="zoom-btn reset-zoom" 
                title="Reset Zoom"
            >
                ⌂
            </button>
        </div>
        <canvas 
            class="canvas" 
            ref="canvas" 
            :width="area.width" 
            :height="area.height" 
            tabindex="0"
            @contextmenu.prevent
        ></canvas>
    </div>
</template>

<script>
import { fabric } from 'fabric';

// Constants
const TYPE_COLORS = {
    resource: { fill: '#3a0ca3a8', stroke: '#3a0ca3' }
};

const ZOOM_CONFIG = {
    min: 0.1,
    max: 10,
    step: 1.2
};

const MOVE_STEP = {
    normal: 1,
    fast: 10
};

export default {
    name: 'SeatMapCanvas',
    props: {
        area: {
            type: Object,
            required: true,
            validator: (area) => area && typeof area.width === 'number' && typeof area.height === 'number'
        },
        slots: {
            type: Array,
            default: () => []
        },
        onSlotSelection: {
            type: Function,
            default: null
        },
        onSlotModification: {
            type: Function,
            default: null
        },
        selectedSlotId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            fabricInstance: null,
            slotIndex: new Map(),
            areaHash: null,
            bgImg: null,
            currentZoom: 1,
            lastPosX: 0,
            lastPosY: 0,
            touchHandlers: {
                initialDistance: 0,
                initialZoom: 1
            },
            eventListeners: new Map()
        };
    },
    computed: {
        selectedSlot() {
            return this.selectedSlotId 
                ? this.slots.find(slot => slot.id === this.selectedSlotId) 
                : null;
        },
        minZoom() {
            return ZOOM_CONFIG.min;
        },
        maxZoom() {
            return ZOOM_CONFIG.max;
        }
    },
    methods: {
        // ===== SYNC METHODS =====
        syncAll() {
            this.syncArea();
            this.syncObjects();
        },

        syncArea() {
            const areaHash = `${this.area.width}x${this.area.height}`;
            if (this.areaHash !== areaHash) {
                this.areaHash = areaHash;
                this.fabricInstance.setWidth(this.area.width);
                this.fabricInstance.setHeight(this.area.height);
            }

            this.syncBackgroundImage();
        },

        syncBackgroundImage() {
            const url = this.getBackgroundImageUrl();
            if (this.bgImg !== url) {
                this.bgImg = url;
                if (url) {
                    this.loadBackgroundImage(url);
                }
            }
        },

        getBackgroundImageUrl() {
            return this.area.bgImage?.styles?.find(style => style.style === '_')?.url || null;
        },

        loadBackgroundImage(url) {
            fabric.Image.fromURL(url, (img, isError) => {
                if (isError) {
                    console.warn('Failed to load background image:', url);
                    return;
                }
                img.scaleToWidth(this.area.width);
                this.fabricInstance.setBackgroundImage(img, () => {
                    this.fabricInstance.renderAll();
                });
            });
        },

        syncObjects() {
            const currentSlotIds = new Set(this.slots.map(slot => slot.id));
            const existingSlotIds = new Set(this.slotIndex.keys());

            // Add or update existing slots
            this.slots.forEach(slot => {
                this.syncSlot(slot);
            });

            // Remove slots that no longer exist
            existingSlotIds.forEach(slotId => {
                if (!currentSlotIds.has(slotId)) {
                    this.removeSlot(slotId);
                }
            });
        },

        syncSlot(slot) {
            const existingObject = this.slotIndex.get(slot.id);
            
            if (!existingObject) {
                // Create new object
                this.slotIndex.set(slot.id, this.createSlotObject(slot));
            } else if (existingObject.shapeType !== slot.shapeType) {
                // Update object if type changed
                this.fabricInstance.remove(existingObject);
                this.slotIndex.set(slot.id, this.createSlotObject(slot));
                this.restoreSelection();
            }
        },

        removeSlot(slotId) {
            const object = this.slotIndex.get(slotId);
            if (object) {
                this.fabricInstance.remove(object);
                this.slotIndex.delete(slotId);
            }
        },

        restoreSelection() {
            if (this.selectedSlotId && this.slotIndex.has(this.selectedSlotId)) {
                this.fabricInstance.setActiveObject(this.slotIndex.get(this.selectedSlotId));
            }
        },
        // ===== OBJECT CREATION =====
        createSlotObject(slot) {
            const { type, shapeType } = slot;
            const colors = TYPE_COLORS[type] || TYPE_COLORS.resource;
            
            const obj = this.createFabricObject(slot, colors);
            if (!obj) return null;

            this.setupObjectEvents(obj, slot);
            this.fabricInstance.add(obj);
            
            return obj;
        },

        createFabricObject(slot, colors) {
            const { shapeType } = slot;
            const { fill, stroke } = colors;

            switch (shapeType) {
                case 'rect':
                    return new fabric.Rect({ 
                        ...slot.rect, 
                        fill, 
                        stroke,
                        strokeWidth: 2
                    });
                case 'circle':
                    return new fabric.Circle({ 
                        ...slot.circle, 
                        fill, 
                        stroke,
                        strokeWidth: 2
                    });
                default:
                    console.warn(`Unsupported shape type: ${shapeType}`);
                    return null;
            }
        },

        setupObjectEvents(obj, slot) {
            // Object modification handler
            obj.on('modified', () => {
                this.handleObjectModification(obj, slot);
            });
            
            // Object interaction handlers
            obj.on('mousedown', () => {
                this.fabricInstance.objectDragging = true;
            });
            
            obj.on('mouseup', () => {
                this.fabricInstance.objectDragging = false;
            });
        },

        handleObjectModification(obj, slot) {
            if (!this.onSlotModification) return;

            const newValues = this.calculateObjectValues(obj, slot.shapeType);
            this.onSlotModification(slot, newValues);
        },

        calculateObjectValues(obj, shapeType) {
            const baseValues = {
                left: Math.round(obj.left),
                top: Math.round(obj.top)
            };

            if (shapeType === 'rect') {
                return {
                    ...baseValues,
                    width: Math.round(obj.width * obj.scaleX),
                    height: Math.round(obj.height * obj.scaleY),
                    angle: obj.angle
                };
            } else if (shapeType === 'circle') {
                return {
                    ...baseValues,
                    radius: Math.round(obj.radius * obj.scaleX)
                };
            }

            return baseValues;
        },
        // ===== ZOOM METHODS =====
        zoomIn() {
            this.setZoom(this.currentZoom * ZOOM_CONFIG.step);
        },
        
        zoomOut() {
            this.setZoom(this.currentZoom / ZOOM_CONFIG.step);
        },
        
        resetZoom() {
            this.setZoom(1);
            this.resetPan();
        },
        
        resetPan() {
            const vpt = this.fabricInstance.viewportTransform;
            vpt[4] = 0; // Reset X pan
            vpt[5] = 0; // Reset Y pan
            this.fabricInstance.setViewportTransform(vpt);
        },
        
        setZoom(zoom) {
            const clampedZoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
            if (clampedZoom === this.currentZoom) return;
            
            this.currentZoom = clampedZoom;
            const center = this.fabricInstance.getCenter();
            this.fabricInstance.zoomToPoint(
                new fabric.Point(center.left, center.top), 
                clampedZoom
            );
        },
        // ===== PANNING METHODS =====
        enablePanning() {
            this.fabricInstance.isDragging = false;
            this.fabricInstance.defaultCursor = 'grab';
            
            const canvas = this.$refs.canvas;
            const container = this.$el;
            
            // Create bound event handlers for proper cleanup
            const mouseDownHandler = this.createMouseDownHandler();
            const mouseMoveHandler = this.createMouseMoveHandler(canvas);
            const mouseUpHandler = this.createMouseUpHandler();
            const mouseLeaveHandler = this.createMouseLeaveHandler();
            
            // Store handlers for cleanup
            this.eventListeners.set('mousedown', mouseDownHandler);
            this.eventListeners.set('mousemove', mouseMoveHandler);
            this.eventListeners.set('mouseup', mouseUpHandler);
            this.eventListeners.set('mouseleave', mouseLeaveHandler);
            
            // Add events to both canvas and container
            [canvas, container].forEach(element => {
                element.addEventListener('mousedown', mouseDownHandler);
                element.addEventListener('mousemove', mouseMoveHandler);
                element.addEventListener('mouseup', mouseUpHandler);
                element.addEventListener('mouseleave', mouseLeaveHandler);
            });
        },

        createMouseDownHandler() {
            return (e) => {
                const pointer = this.fabricInstance.getPointer(e);
                const target = this.fabricInstance.findTarget(pointer, true);
                
                if (!target) {
                    // Background click - start panning
                    this.fabricInstance.isDragging = true;
                    this.fabricInstance.defaultCursor = 'grabbing';
                    this.lastPosX = e.clientX;
                    this.lastPosY = e.clientY;
                    e.preventDefault();
                } else {
                    // Object click - start object dragging
                    this.fabricInstance.isDragging = false;
                    this.fabricInstance.objectDragging = true;
                }
            };
        },

        createMouseMoveHandler(canvas) {
            return (e) => {
                if (this.fabricInstance.isDragging && !this.fabricInstance.objectDragging) {
                    this.handlePanning(e);
                } else {
                    this.updateCursor(canvas, e);
                }
            };
        },

        createMouseUpHandler() {
            return () => {
                this.fabricInstance.isDragging = false;
                this.fabricInstance.objectDragging = false;
                this.fabricInstance.defaultCursor = 'grab';
            };
        },

        createMouseLeaveHandler() {
            return () => {
                this.fabricInstance.isDragging = false;
                this.fabricInstance.objectDragging = false;
                this.fabricInstance.defaultCursor = 'grab';
            };
        },

        handlePanning(e) {
            const deltaX = e.clientX - this.lastPosX;
            const deltaY = e.clientY - this.lastPosY;
            
            const vpt = this.fabricInstance.viewportTransform;
            vpt[4] += deltaX;
            vpt[5] += deltaY;
            
            this.fabricInstance.setViewportTransform(vpt);
            this.fabricInstance.requestRenderAll();
            
            this.lastPosX = e.clientX;
            this.lastPosY = e.clientY;
        },

        updateCursor(canvas, e) {
            const pointer = this.fabricInstance.getPointer(e);
            const target = this.fabricInstance.findTarget(pointer, true);
            canvas.style.cursor = target ? 'move' : 'grab';
        },
        

        // ===== TOUCH METHODS =====
        handleTouchStart(e) {
            if (e.touches.length === 2) {
                this.touchHandlers.initialDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
                this.touchHandlers.initialZoom = this.currentZoom;
            }
        },
        
        handleTouchMove(e) {
            if (e.touches.length === 2) {
                e.preventDefault();
                
                const currentDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
                const scale = currentDistance / this.touchHandlers.initialDistance;
                const newZoom = this.touchHandlers.initialZoom * scale;
                
                this.setZoom(newZoom);
            }
        },
        
        getTouchDistance(touch1, touch2) {
            const dx = touch1.clientX - touch2.clientX;
            const dy = touch1.clientY - touch2.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },
        // ===== SELECTION METHODS =====
        handleObjectSelection(selectedObject) {
            if (!this.onSlotSelection) return;

            if (selectedObject) {
                const slotId = this.findSlotIdByObject(selectedObject);
                if (slotId) {
                    const slot = this.slots.find(s => s.id === slotId);
                    if (slot) {
                        this.onSlotSelection(slot);
                        this.$refs.canvas.focus();
                    }
                }
            } else {
                this.onSlotSelection(null);
            }
        },

        findSlotIdByObject(object) {
            for (const [slotId, fabricObject] of this.slotIndex.entries()) {
                if (fabricObject === object) {
                    return slotId;
                }
            }
            return null;
        },

        // ===== KEYBOARD METHODS =====
        setupKeyboardListeners() {
            const keyDownHandler = this.createKeyDownHandler();
            this.eventListeners.set('keydown', keyDownHandler);
            document.addEventListener('keydown', keyDownHandler);
        },
        
        createKeyDownHandler() {
            return (e) => {
                if (!this.shouldHandleKeyDown(e)) return;
                
                const moveStep = e.shiftKey ? MOVE_STEP.fast : MOVE_STEP.normal;
                const newPosition = this.calculateNewPosition(e, moveStep);
                
                if (newPosition) {
                    this.moveSelectedObject(newPosition);
                }
            };
        },

        shouldHandleKeyDown(e) {
            // Only handle arrow keys when a slot is selected
            if (!this.selectedSlotId) return false;
            
            const activeObject = this.fabricInstance.getActiveObject();
            if (!activeObject) return false;
            
            // Check if we're in an input field or textarea
            const activeElement = document.activeElement;
            if (activeElement && this.isInputElement(activeElement)) {
                return false;
            }
            
            return ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key);
        },

        isInputElement(element) {
            return element.tagName === 'INPUT' || 
                   element.tagName === 'TEXTAREA' || 
                   element.contentEditable === 'true';
        },

        calculateNewPosition(e, moveStep) {
            const activeObject = this.fabricInstance.getActiveObject();
            if (!activeObject) return null;

            let newLeft = activeObject.left;
            let newTop = activeObject.top;
            
            switch(e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    newLeft -= moveStep;
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    newLeft += moveStep;
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    newTop -= moveStep;
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    newTop += moveStep;
                    break;
                default:
                    return null;
            }
            
            return { left: newLeft, top: newTop };
        },

        moveSelectedObject(newPosition) {
            const activeObject = this.fabricInstance.getActiveObject();
            if (!activeObject) return;

            activeObject.set(newPosition);
            activeObject.setCoords();
            this.fabricInstance.renderAll();
            
            // Update slot data
            this.updateSlotPosition(newPosition);
        },

        updateSlotPosition(newPosition) {
            const slot = this.slots.find(s => s.id === this.selectedSlotId);
            if (!slot || !this.onSlotModification) return;

            const shape = slot.rect ?? slot.circle;
            const newValues = {
                ...shape,
                left: Math.round(newPosition.left),
                top: Math.round(newPosition.top),
            };
            
            this.onSlotModification(slot, newValues);
        },
        // ===== EVENT MANAGEMENT =====
        setupEventListeners() {
            const canvas = this.$refs.canvas;
            
            // Create and store event handlers
            const contextMenuHandler = (e) => e.preventDefault();
            const clickHandler = () => canvas.focus();
            const wheelHandler = this.createWheelHandler();
            
            // Store handlers for cleanup
            this.eventListeners.set('contextmenu', contextMenuHandler);
            this.eventListeners.set('click', clickHandler);
            this.eventListeners.set('wheel', wheelHandler);
            this.eventListeners.set('touchstart', this.handleTouchStart);
            this.eventListeners.set('touchmove', this.handleTouchMove);
            
            // Add event listeners
            canvas.addEventListener('contextmenu', contextMenuHandler);
            canvas.addEventListener('click', clickHandler);
            canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
            canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
            
            // Add wheel events to both canvas and container
            [canvas, this.$el].forEach(element => {
                element.addEventListener('wheel', wheelHandler, { passive: false });
            });
            
            this.setupFabricEventListeners();
            this.setupKeyboardListeners();
        },

        createWheelHandler() {
            return (e) => {
                e.preventDefault();
                e.stopPropagation();
                const delta = e.deltaY > 0 ? 0.9 : 1.1;
                this.setZoom(this.currentZoom * delta);
            };
        },

        setupFabricEventListeners() {
            const selectionCreatedHandler = (e) => this.handleObjectSelection(e.selected[0]);
            const selectionUpdatedHandler = (e) => this.handleObjectSelection(e.selected[0]);
            const selectionClearedHandler = () => this.handleObjectSelection(null);
            
            this.fabricInstance.on('selection:created', selectionCreatedHandler);
            this.fabricInstance.on('selection:updated', selectionUpdatedHandler);
            this.fabricInstance.on('selection:cleared', selectionClearedHandler);
            
            // Store fabric event handlers for cleanup
            this.eventListeners.set('fabric:selection:created', selectionCreatedHandler);
            this.eventListeners.set('fabric:selection:updated', selectionUpdatedHandler);
            this.eventListeners.set('fabric:selection:cleared', selectionClearedHandler);
        },
        
        removeEventListeners() {
            const canvas = this.$refs.canvas;
            
            // Remove DOM event listeners
            this.eventListeners.forEach((handler, eventType) => {
                if (eventType.startsWith('fabric:')) return; // Handle fabric events separately
                
                if (eventType === 'wheel') {
                    [canvas, this.$el].forEach(element => {
                        element.removeEventListener('wheel', handler);
                    });
                } else if (eventType === 'keydown') {
                    document.removeEventListener('keydown', handler);
                } else if (['mousedown', 'mousemove', 'mouseup', 'mouseleave'].includes(eventType)) {
                    [canvas, this.$el].forEach(element => {
                        element.removeEventListener(eventType, handler);
                    });
                } else {
                    canvas.removeEventListener(eventType, handler);
                }
            });
            
            // Remove fabric event listeners
            if (this.fabricInstance) {
                this.fabricInstance.off('selection:created');
                this.fabricInstance.off('selection:updated');
                this.fabricInstance.off('selection:cleared');
            }
            
            // Clear event listeners map
            this.eventListeners.clear();
        },

        // ===== LIFECYCLE HELPERS =====
        initializeCanvas() {
            this.fabricInstance = new fabric.Canvas(this.$refs.canvas);
            this.configureCanvas();
            
            this.$nextTick(() => {
                this.setupEventListeners();
                this.enablePanning();
                this.syncAll();
            });
        },
        
        configureCanvas() {
            this.fabricInstance.selection = true;
            this.fabricInstance.defaultCursor = 'grab';
            this.fabricInstance.isDrawingMode = false;
            this.fabricInstance.preserveObjectStacking = true;
        },
        
        cleanup() {
            this.removeEventListeners();
            if (this.fabricInstance) {
                this.fabricInstance.dispose();
                this.fabricInstance = null;
            }
            this.slotIndex.clear();
            this.eventListeners.clear();
        },

        // ===== WATCHER HELPERS =====
        updateSelection(slotId) {
            if (!slotId) {
                this.fabricInstance.discardActiveObject();
                return;
            }
            
            const object = this.slotIndex.get(slotId);
            if (object && this.fabricInstance.getActiveObject() !== object) {
                this.fabricInstance.setActiveObject(object);
            }
        }
    },
    
    // ===== LIFECYCLE METHODS =====
    mounted() {
        try {
            this.initializeCanvas();
        } catch (error) {
            console.error('Failed to initialize canvas:', error);
        }
    },
    
    beforeDestroy() {
        this.cleanup();
    },
    
    // ===== WATCHERS =====
    watch: {
        area: {
            handler() {
                this.syncArea();
            },
            deep: true
        },
        slots: {
            handler() {
                this.syncObjects();
            },
            deep: true
        },
        selectedSlotId: {
            handler(newSlotId) {
                this.updateSelection(newSlotId);
            }
        }
    }
}
</script>

<style scoped lang="scss">
.seat-map-canvas {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    
    .canvas-controls {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 10;
        display: flex;
        flex-direction: column;
        gap: 5px;
        
        .zoom-btn {
            width: 40px;
            height: 40px;
            border: none;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.9);
            color: #333;
            font-size: 18px;
            font-weight: bold;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            
            &:hover:not(:disabled) {
                background: rgba(255, 255, 255, 1);
                transform: scale(1.1);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            }
            
            &:active:not(:disabled) {
                transform: scale(0.95);
            }
            
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
                transform: none;
            }
            
            &.zoom-in {
                background: rgba(76, 175, 80, 0.9);
                color: white;
                
                &:hover:not(:disabled) {
                    background: rgba(76, 175, 80, 1);
                }
            }
            
            &.zoom-out {
                background: rgba(244, 67, 54, 0.9);
                color: white;
                
                &:hover:not(:disabled) {
                    background: rgba(244, 67, 54, 1);
                }
            }
            
            &.reset-zoom {
                background: rgba(33, 150, 243, 0.9);
                color: white;
                font-size: 16px;
                
                &:hover:not(:disabled) {
                    background: rgba(33, 150, 243, 1);
                }
            }
        }
    }
    
    .canvas {
        background: #c9bde6 url(/images/grid.png);
        outline: none;
        display: block;
        width: 100%;
        height: 100%;
        
        &:active {
            cursor: grabbing;
        }
        
        &:focus {
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.5);
        }
    }
}
</style>
